/**
 * Framework-free mirror of the mobile app's design tokens (constants/theme.ts).
 * The mobile file imports `react-native`, so it can't be shared directly — these
 * values are kept in sync by hand. Every number/hex here is copied from the
 * mobile source; change it there first, then mirror it here AND in the CSS
 * variables (src/index.css) + Tailwind config (tailwind.config.js).
 *
 * How to consume
 * --------------
 * - Tailwind classes (preferred for layout/colour): every `Colors` token is a
 *   theme-aware CSS variable `--color-<kebab-name>` and a Tailwind colour
 *   `<kebab-name>` (e.g. `bg-surface-inner`, `text-risk-safe`,
 *   `bg-premium-soft`, `text-button-secondary-text`). Opacity modifiers work
 *   (`bg-risk-safe/10`) via color-mix.
 * - Nutrient colours: CSS var `--color-n-<kebab-key>` / Tailwind `n-<kebab-key>`
 *   (see `NutrientCssVar` below for the full key → name table).
 * - JS (inline styles, SVG, charts, alpha tints): `useThemeColor({}, name)`,
 *   `useNutrientColors()`, `useRiskColors()` (src/hooks/*) return the hex for
 *   the active scheme so `withAlpha()` works on them.
 *
 * The CSS variables carry the exact mobile values. (`--color-text-muted` and
 * `--color-border` used to be one step darker on the web; the Sept 2026 parity
 * port restored the app's values so cards and hairlines match the phone.)
 */

// Font families — the web loads Inter 400/500/600/700 from Google Fonts
// (index.html). Mobile's Inter_400Regular etc. are separate RN font files;
// on the web they're one family selected by weight.
export const FontFamily = {
  regular: 'Inter',
  medium: 'Inter',
  semiBold: 'Inter',
  bold: 'Inter',
  system: 'system-ui',
} as const;

/** Weight that goes with each mobile FontFamily entry. */
export const FontWeight = {
  regular: 400,
  medium: 500,
  semiBold: 600,
  bold: 700,
} as const;

// Primary brand colors - Axel mint-emerald
const primaryMint = '#0F8A63'; // primary actions (light mode)
const primaryMintDeep = '#0D6B4B'; // gradient deep stop / pressed states

// Accent colors - Axel's body mint
const accentMint = '#4FD6AC'; // dark-mode primary, highlights
const accentMintLight = '#7FE3C4';

// Brand ground - matches app icon / splash
const brandEvergreen = '#11512C';

// Premium accent - Axel's gill purple. Pro/premium surfaces ONLY;
// never use for medical/semantic meaning.
const premiumPurple = '#7C3AED';
const premiumPurpleBright = '#A78BFA';

// Safety colors for risk levels
const safeGreen = '#22C55E';
const moderateYellow = '#F59E0B';
const highRiskRed = '#EF4444';

// Risk / data palette — the single source of truth for the safe / moderate /
// limit triad. LIGHT values are the classic hexes; DARK values are one step
// brighter so the triad stays legible on OLED black. `riskNone` is the
// "no data / not rated" neutral. These colours ONLY ever mean
// safe / moderate / limit — never decoration.
const riskSafeDark = '#4ADE80'; // green-400
const riskModerateDark = '#FBBF24'; // amber-400
const riskLimitDark = '#F87171'; // red-400
const riskNoneLight = '#D1D5DB'; // gray-300
const riskNoneDark = '#4B5563'; // gray-600

export const Colors = {
  light: {
    // Text colors
    text: '#1E293B',
    textSecondary: '#64748B',
    textMuted: '#94A3B8',

    // Backgrounds
    background: '#FAFBFC',
    backgroundSecondary: '#ECFDF5', // Light mint tint
    backgroundElevated: '#FFFFFF',

    // Primary colors
    primary: primaryMint,
    primaryDark: primaryMintDeep,
    accent: accentMint,
    brand: brandEvergreen,

    // Premium (Pro surfaces only)
    premium: premiumPurple,
    premiumSoft: '#F5F3FF',
    premiumStrong: premiumPurple, // filled Pro badge that holds white text

    // Semantic colors for meal safety levels
    safe: safeGreen,
    moderate: moderateYellow,
    trigger: highRiskRed,

    // Risk / data palette
    riskSafe: safeGreen,
    riskModerate: moderateYellow,
    riskLimit: highRiskRed,
    riskNone: riskNoneLight,

    // UI elements
    border: '#E2E8F0',
    borderLight: '#F1F5F9',
    card: '#FFFFFF',
    // `surface` is the card plane; `surfaceInner` is the recessed chip/badge
    // plane on top of it.
    surface: '#FFFFFF',
    surfaceInner: '#F1F5F9',
    shadow: 'rgba(0, 0, 0, 0.08)',
    // Backdrop behind bottom-sheet modals.
    scrim: 'rgba(0, 0, 0, 0.7)',

    // Tab bar
    tint: primaryMint,
    icon: '#94A3B8',
    tabIconDefault: '#94A3B8',
    tabIconSelected: primaryMint,
    tabBackground: '#FFFFFF',

    // Chat bubbles
    userBubble: primaryMint,
    userBubbleText: '#FFFFFF',
    aiBubble: '#ECFDF5',
    aiBubbleText: '#1E293B',

    // Buttons
    buttonPrimary: primaryMint,
    // Filled CTA that must hold white text in BOTH schemes.
    buttonPrimaryStrong: primaryMint,
    buttonPrimaryText: '#FFFFFF',
    buttonSecondary: '#ECFDF5',
    buttonSecondaryText: primaryMintDeep,
    buttonDisabled: '#E2E8F0',
    buttonDisabledText: '#94A3B8',

    // Input
    inputBackground: '#FFFFFF',
    inputBorder: '#E2E8F0',
    inputText: '#1E293B',
    inputPlaceholder: '#94A3B8',

    // Status
    error: '#DC2626',
    warning: '#D97706',
    info: primaryMint,
    success: safeGreen,
  },
  dark: {
    // Text colors
    text: '#FFFFFF',
    textSecondary: '#A0A0A0',
    textMuted: '#8A8A8A',

    // Backgrounds - True black OLED friendly
    background: '#000000',
    backgroundSecondary: '#0A1711',
    backgroundElevated: '#1C1C1C',

    // Primary colors
    primary: accentMint,
    primaryDark: primaryMint,
    accent: accentMintLight,
    brand: brandEvergreen,

    // Premium (Pro surfaces only)
    premium: premiumPurpleBright,
    premiumSoft: '#1D1533',
    premiumStrong: premiumPurple,

    // Semantic colors for meal safety levels
    safe: safeGreen,
    moderate: moderateYellow,
    trigger: highRiskRed,

    // Risk / data palette
    riskSafe: riskSafeDark,
    riskModerate: riskModerateDark,
    riskLimit: riskLimitDark,
    riskNone: riskNoneDark,

    // UI elements
    border: '#2A2A2A',
    borderLight: '#1C1C1C',
    card: '#121212',
    surface: '#121212',
    surfaceInner: '#1C1C1C',
    shadow: 'rgba(0, 0, 0, 0.5)',
    scrim: 'rgba(0, 0, 0, 0.7)',

    // Tab bar
    tint: accentMint,
    icon: '#6B6B6B',
    tabIconDefault: '#6B6B6B',
    tabIconSelected: accentMint,
    tabBackground: '#121212',

    // Chat bubbles
    userBubble: primaryMint,
    userBubbleText: '#FFFFFF',
    aiBubble: '#1C1C1C',
    aiBubbleText: '#FFFFFF',

    // Buttons
    buttonPrimary: primaryMint,
    buttonPrimaryStrong: primaryMint,
    buttonPrimaryText: '#FFFFFF',
    buttonSecondary: '#1C1C1C',
    buttonSecondaryText: '#FFFFFF',
    buttonDisabled: '#2A2A2A',
    buttonDisabledText: '#6B6B6B',

    // Input
    inputBackground: '#121212',
    inputBorder: '#2A2A2A',
    inputText: '#FFFFFF',
    inputPlaceholder: '#6B6B6B',

    // Status
    error: '#EF4444',
    warning: '#F59E0B',
    info: accentMint,
    success: safeGreen,
  },
} as const;

export type ColorName = keyof typeof Colors.light & keyof typeof Colors.dark;
export type ColorScheme = 'light' | 'dark';

/**
 * NutrientColors — ONE colour per nutrient, used everywhere a nutrient is
 * coloured (rings, meal totals, trend charts, meal summary, progress).
 *
 * Rules: no purple (Pro only), and no hue that could be read as the risk
 * triad — risk is shown by the `risk*` tokens, never by a nutrient's own
 * colour. Consume through `useNutrientColors()` (JS) or the `n-*` Tailwind
 * colours / `--color-n-*` CSS variables.
 */
export const NutrientColors = {
  light: {
    calories: '#2563EB', // blue-600
    addedSugar: '#DB2777', // pink-600
    sugars: '#EC4899', // pink-500
    carbs: '#0891B2', // cyan-600
    fiber: '#0F766E', // teal-700 — a positive target, brand-adjacent
    fat: '#A8784F', // tan
    saturatedFat: '#8B5A2B', // butter-brown
    transFat: '#57534E', // stone-600
    sodium: '#475569', // slate-600 (salt)
    protein: '#0369A1', // sky-700
    cholesterol: '#78716C', // stone-500
    fluid: '#0284C7', // sky-600 (water)
    coffee: '#6F4E37', // coffee brown
  },
  dark: {
    calories: '#60A5FA',
    addedSugar: '#F472B6',
    sugars: '#F9A8D4',
    carbs: '#22D3EE',
    fiber: '#2DD4BF',
    fat: '#D6A77A',
    saturatedFat: '#C69C6D',
    transFat: '#A8A29E',
    sodium: '#94A3B8',
    protein: '#7DD3FC',
    cholesterol: '#D6D3D1',
    fluid: '#38BDF8',
    coffee: '#C8A27A',
  },
} as const;

export type NutrientColorKey = keyof typeof NutrientColors.light;

/**
 * Nutrient colour key → theme-aware CSS variable. The Tailwind colour is the
 * same name without `--color-`:
 *   calories → n-calories        addedSugar → n-added-sugar   sugars → n-sugars
 *   carbs → n-carbs              fiber → n-fiber              fat → n-fat
 *   saturatedFat → n-saturated-fat  transFat → n-trans-fat    sodium → n-sodium
 *   protein → n-protein          cholesterol → n-cholesterol  fluid → n-fluid
 *   coffee → n-coffee
 * e.g. `text-n-added-sugar`, `bg-n-fiber/10`, `style={{ color: NutrientCssVar.fiber }}`.
 */
export const NutrientCssVar: Record<NutrientColorKey, string> = {
  calories: 'var(--color-n-calories)',
  addedSugar: 'var(--color-n-added-sugar)',
  sugars: 'var(--color-n-sugars)',
  carbs: 'var(--color-n-carbs)',
  fiber: 'var(--color-n-fiber)',
  fat: 'var(--color-n-fat)',
  saturatedFat: 'var(--color-n-saturated-fat)',
  transFat: 'var(--color-n-trans-fat)',
  sodium: 'var(--color-n-sodium)',
  protein: 'var(--color-n-protein)',
  cholesterol: 'var(--color-n-cholesterol)',
  fluid: 'var(--color-n-fluid)',
  coffee: 'var(--color-n-coffee)',
};

/**
 * Typography scale — same keys and numbers as mobile. Each entry is a valid
 * React `style` object (fontSize/letterSpacing in px; lineHeight as a px
 * string because a bare number is a multiplier in CSS). Entries that have no
 * lineHeight on mobile use `'normal'` — the font's natural line height, which
 * is what React Native renders (the browser default from Tailwind preflight
 * would be 1.5).
 *
 * Tailwind font-size utilities carry the same values: `text-h1` … `text-caption`,
 * plus `text-card-title`, `text-meta`, `text-stat-value`, `text-chat-message`,
 * `text-verdict-title`, `text-verdict-subtitle`, `text-section-label`,
 * `text-food-name`, `text-insight`, `text-health-score`, `text-score-label`.
 * (Add `tabular-nums` next to `text-stat-value` — a font-size utility can't set it.)
 */
export const Typography = {
  // Headings
  h1: { fontSize: 32, fontWeight: 700, lineHeight: '40px', letterSpacing: -0.5 },
  h2: { fontSize: 24, fontWeight: 600, lineHeight: '32px', letterSpacing: -0.25 },
  h3: { fontSize: 20, fontWeight: 600, lineHeight: '28px' },
  h4: { fontSize: 18, fontWeight: 600, lineHeight: '24px' },
  // Body text
  body: { fontSize: 16, fontWeight: 400, lineHeight: '24px' },
  bodyLarge: { fontSize: 18, fontWeight: 400, lineHeight: '28px' },
  bodySmall: { fontSize: 14, fontWeight: 400, lineHeight: '20px' },
  // UI elements
  button: { fontSize: 16, fontWeight: 600, lineHeight: '24px' },
  caption: { fontSize: 12, fontWeight: 400, lineHeight: '16px' },
  // Card header line ("This week", "Today's Liver Score", …)
  cardTitle: { fontSize: 16, fontWeight: 600, lineHeight: 'normal', letterSpacing: -0.2 },
  // Secondary line under a title: counts, timestamps, units
  meta: { fontSize: 12, fontWeight: 400, lineHeight: 'normal' },
  // The one large number a card leads with (tabular so digits don't jitter)
  statValue: {
    fontSize: 32,
    fontWeight: 700,
    lineHeight: 'normal',
    letterSpacing: -0.5,
    fontVariantNumeric: 'tabular-nums',
  },
  // Chat specific
  chatMessage: { fontSize: 16, fontWeight: 400, lineHeight: '22px' },
  // Meal Analysis - Refined Minimal
  verdictTitle: { fontSize: 20, fontWeight: 700, lineHeight: 'normal', letterSpacing: -0.3 },
  verdictSubtitle: { fontSize: 14, fontWeight: 400, lineHeight: '18px' },
  sectionLabel: { fontSize: 11, fontWeight: 600, lineHeight: 'normal', letterSpacing: 0.8 },
  foodName: { fontSize: 15, fontWeight: 600, lineHeight: 'normal' },
  insightText: { fontSize: 15, fontWeight: 400, lineHeight: '22px' },
  // Score display
  healthScore: { fontSize: 48, fontWeight: 700, lineHeight: 'normal', letterSpacing: -1 },
  scoreLabel: { fontSize: 12, fontWeight: 500, lineHeight: 'normal', letterSpacing: 0.5 },
} as const;

// Spacing scale (based on 4px grid). Tailwind: p-xs, gap-smd, mt-mlg, …
export const Spacing = {
  xs: 4,
  sm: 8,
  smd: 12,
  md: 16,
  mlg: 20,
  lg: 24,
  xl: 32,
  xxl: 48,
  xxxl: 64,
} as const;

// Border radius scale. Tailwind: rounded-sm … rounded-xxl, rounded-full.
export const BorderRadius = {
  none: 0,
  sm: 4,
  md: 8,
  lg: 12,
  xl: 16,
  xxl: 24,
  full: 9999,
} as const;

/**
 * Elevation, as CSS box-shadow strings (offset-x offset-y blur colour), using
 * the same convention mobile uses for its Android `boxShadow` card lift
 * (shadowRadius → blur). Tailwind: shadow-sm / md / lg / xl / card / none.
 */
export const Shadows = {
  none: 'none',
  sm: '0px 1px 2px rgba(0,0,0,0.05)',
  md: '0px 2px 4px rgba(0,0,0,0.08)',
  lg: '0px 4px 8px rgba(0,0,0,0.1)',
  xl: '0px 8px 16px rgba(0,0,0,0.12)',
  // The standard card lift.
  card: '0px 2px 8px rgba(0,0,0,0.06)',
} as const;

// Responsive layout constraints (content is capped and centered on wide
// screens). Tailwind: max-w-content.
export const Layout = {
  contentMaxWidth: 600,
} as const;

// Layout spacing for specific screens
export const AnalysisLayout = {
  screenPadding: 20,
  heroHeight: 280,
  heroToFoods: 24,
  foodsToInsights: 32,
  insightGap: 16,
  insightsToActions: 24,
  foodCardWidth: 160,
  foodCardMinWidth: 140,
  foodCardMaxWidth: 180,
} as const;
